import { Add, CopyAll, Delete } from '@mui/icons-material'
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Theme,
	Typography
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { percent } from 'csx'
import { action } from 'mobx'
import { observer, useLocalStore } from 'mobx-react'
import { useCallback, useEffect, useState } from 'react'
import {
	FormPackageLinkModel,
	FormPackageLinkType,
} from '../../../../../../../../api/DTOtemp'
import AuthenticationProviderInstancesClient from '../../../../../../../../api/clients/authenticationProviders/AuthenticationProviderInstancesClient'
import AuthenticationProvidersClient from '../../../../../../../../api/clients/authenticationProviders/AuthenticationProvidersClient'
import { AuthenticationProviderMetadata } from '../../../../../../../../api/clients/authenticationProviders/DTO'
import { FormPackageLinksClient } from '../../../../../../../../api/clients/identity'
import { FullscreenSpinner } from '../../../../../../../../components/feedback/circular'
import { usePrompts } from '../../../../../../../../services/notifications/PromptService'
import { toastService } from '../../../../../../../../services/notifications/ToastService'
import AdministrationPageContainer from '../../../../../AdministrationPageContainer'
import { AdministrationPageTabs } from '../../../../../AdministrationPageTabs'
import LicenseCheck from '../../../../../Licensing/LicenseCheck'
import { FormPackageAdministrationPageProps } from '../../FormPackagePage'

export { }

export const AnonymousLinkPage = observer(
	(props: FormPackageAdministrationPageProps) => {
		const promptService = usePrompts()

		const styles = useStyles()

		const localStore = useLocalStore(() => ({
			loadingLinks: false,
			links: [] as FormPackageLinkModel[],
			createLinkOpen: false
		}))

		const refreshLinks = useCallback(action(() => {
			localStore.loadingLinks = true
			const linkClient = new FormPackageLinksClient()

			return linkClient
				.getFormPackageLinks(props.packageId)
				.then(action(v =>
					localStore.links = v.data
				)).finally(action(() =>
					localStore.loadingLinks = false
				))
		}), [])

		const deleteLink = useCallback(action((linkId: number) => {
			localStore.loadingLinks = true
			const linkClient = new FormPackageLinksClient()

			linkClient.deleteLink(linkId).then((v) => {
				toastService.displayToast({
					area: 'global',
					message: 'Link Deleted',
					severity: 'success',
				})
				refreshLinks()
			})
		}), [])

		const handleLinkOpen = action(() =>
			localStore.createLinkOpen = true
		)

		const handleLinkClose = action(() => {
			localStore.createLinkOpen = false
			refreshLinks()
		})

		useEffect(() => {
			refreshLinks()
		}, [])

		return (
			<AdministrationPageContainer
				title={'Anonymous Links'}
				actions={
					<>
						<div>
							<Button
								variant="contained"
								startIcon={<Add />}
								onClick={handleLinkOpen}
							>
								Create Link
							</Button>
						</div>
						{localStore.createLinkOpen && (
							<CreateLinkModal
								formPackageId={props.packageId}
								onClose={handleLinkClose}
							/>
						)}
					</>
				}
				tabs={props.tabLabels}
				currentTab={4}
				onTabChanged={props.onTabChanged}
				hideAttachmentsTab={props.hideAttachmentsTab}
			>
				<div className={styles.formContentRoot}>
					<Alert severity="warning">
						Creating a link with this page will allow anyone who uses the link
						to access this form package. Always ensure that any authentication
						providers that will be used for creating anonymous links are
						properly secured for public access.
					</Alert>

					{localStore.loadingLinks ?? <FullscreenSpinner />}

					<Box paddingTop={2} flex={1} display={'flex'}>
						{localStore.links.length > 0 ? (
							<TableContainer component={Paper}>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell>Authentication Provider</TableCell>
											<TableCell>Embed Link URL</TableCell>
											<TableCell>Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{localStore.links.map((v) => (
											<TableRow key={v.id}>
												<TableCell>
													<AuthProviderNameCell linkModel={v} />
												</TableCell>
												<TableCell>{synthesizeLink(v.uniqueId)}</TableCell>
												<TableCell>
													<IconButton
														onClick={() => {
															window.navigator.clipboard.writeText(
																synthesizeLink(v.uniqueId),
															)
															toastService.displayToast({
																area: 'global',
																message: 'Link Copied!',
																severity: 'success',
																delay: 1,
															})
														}}
													>
														<CopyAll />
													</IconButton>
													<IconButton
														onClick={() => {
															promptService
																.showDialog((props) => (
																	<Box
																		padding={2}
																		display={'flex'}
																		flexDirection={'column'}
																		gap={2}
																	>
																		<Typography variant="h6">
																			Are you sure you want to delete this link?
																		</Typography>
																		<Typography variant="body2">
																			Deleting this link will mean users will no
																			longer be able to use it to access this
																			form package from an external application.
																		</Typography>

																		<Box
																			display="flex"
																			justifyContent={'flex-end'}
																			gap={1}
																		>
																			<Button
																				variant="contained"
																				onClick={() => props.close('cancel')}
																			>
																				Cancel
																			</Button>
																			<Button
																				variant="contained"
																				color="error"
																				onClick={() => props.close('okay')}
																			>
																				Delete
																			</Button>
																		</Box>
																	</Box>
																))
																.then((result) => {
																	if (result === 'okay') {
																		deleteLink(v.id)
																	}
																})
														}}
													>
														<Delete />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						) : (
							<Box flex="1" alignSelf="center" textAlign="center">
								<Typography variant="h6">
									No Anonymous Links Configured
								</Typography>
							</Box>
						)}
					</Box>
				</div>
			</AdministrationPageContainer>
		)
	},
)

type CreateLinkModalProps = {
	formPackageId: number
	onClose: () => void
}

const CreateLinkModal = observer((props: CreateLinkModalProps) => {
	const [authProviders, setAuthProviders] = useState<
		AuthenticationProviderMetadata[] | undefined
	>(undefined)

	const [selectedAuthProvider, setSelectedAuthenticationProvider] = useState<
		string | number | null
	>(null)

	const [submitting, setSubmitting] = useState<boolean>(false)

	useEffect(() => {
		const authProviderApi = new AuthenticationProviderInstancesClient()
		authProviderApi.getInstanceMetadata().then((v) => {
			setAuthProviders(v.data.filter((v) => v.producesAnonymousLogin))
		})
	}, [])

	const createLinkCallback = useCallback(() => {
		if (selectedAuthProvider === null)
			throw new Error('cannot create link without selected auth provider')

		setSubmitting(true)
		const client = new FormPackageLinksClient()

		client
			.createLink(props.formPackageId, selectedAuthProvider)
			.then((v) => {
				const createdLink = v.data
				const linkString = synthesizeLink(createdLink.uniqueId)

				navigator.clipboard.writeText(linkString)

				toastService.displayToast({
					area: 'global',
					message: 'Link Created and Copied to Clipboard!',
					severity: 'success',
				})
			})
			.finally(() => {
				setSubmitting(false), props.onClose()
			})
	}, [selectedAuthProvider])

	if (authProviders === undefined)
		return (
			<Dialog open={true} onClose={props.onClose}>
				<DialogTitle>Create Form Link</DialogTitle>
				<DialogContent>
					<CircularProgress></CircularProgress>
				</DialogContent>
			</Dialog>
		)

	return (
		<Dialog onClose={props.onClose} open={true}>
			<DialogTitle>Create Form Link</DialogTitle>
			<DialogContent>
				{authProviders === undefined ? (
					<CircularProgress />
				) : (
					<FormControl fullWidth>
						<Typography variant="body1" fontWeight={600}>
							Authentication Provider
						</Typography>
						<Select
							value={selectedAuthProvider ?? ''}
							onChange={(v) => {
								const value = v.target.value
								// if (typeof value === 'string') value = parseInt(value)

								setSelectedAuthenticationProvider(value)
							}}
						>
							{authProviders.map((v) => (
								<MenuItem key={v.id} value={v.id}>
									{v.displayName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}
				<DialogContentText>
					Select an authentication provider to use for anonymous access to this
					form package
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				{submitting && <CircularProgress />}
				<Button
					disabled={submitting || selectedAuthProvider === null}
					startIcon={<Add />}
					variant="contained"
					onClick={createLinkCallback}
				>
					Create
				</Button>
			</DialogActions>
		</Dialog>
	)
})

const AuthProviderNameCell = (props: { linkModel: FormPackageLinkModel }) => {
	const [providerName, setProviderName] = useState<string | undefined>(
		undefined,
	)

	const getInternalProviderName = async (internalProviderId: string) => {
		const client = new AuthenticationProvidersClient()

		const { data: internalProvider } =
			await client.getInternalAuthenticationProvider(internalProviderId)

		return internalProvider.displayName
	}

	const getProviderInstanceName = async (instanceId: number) => {
		const client = new AuthenticationProviderInstancesClient()

		const { data: instance } = await client.getInstance(instanceId)

		return instance.displayName
	}

	const getName = () => {
		return props.linkModel.type === FormPackageLinkType.Internal
			? getInternalProviderName(
				props.linkModel.internalAuthenticationProviderId,
			)
			: getProviderInstanceName(props.linkModel.authenticationProviderId)
	}

	useEffect(() => {
		getName().then((name) => setProviderName(name))
	}, [])

	if (providerName === undefined)
		return (
			<Box display={'flex'} gap={2}>
				{props.linkModel.type === FormPackageLinkType.Internal
					? props.linkModel.internalAuthenticationProviderId
					: props.linkModel.authenticationProviderId}
				<CircularProgress size={'1rem'} />
			</Box>
		)

	return <Box display={'flex'}>{providerName}</Box>
}

const synthesizeLink = (linkId: string) =>
	`${window.location.protocol}//${window.location.host}/_external/_link/${linkId}`

const useStyles = makeStyles((theme: Theme) => ({
	formContentRoot: {
		display: 'flex',
		flexDirection: 'column',

		width: percent(100),
	},

	insetSpacing: {
		margin: theme.spacing(2, 0),
	},
}))
